import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { NV_PATHS, NvButton, NvCheckIcon } from "@/components/navigator";
import { cn } from "@/lib/utils";
import type { InquiryReceipt } from "@/services/navigator/contact-service";

// F05 접수 완료 (Figma ver.7 847:45159). Drawn from the submit response; a reload without it goes HOME.
// ver.7: 제목 아래에 진행 단계(접수 → 검토중 → 답변 완료)를 보여 줘요. 방금 접수했으니 "접수"가 현재 단계이고, 단계 아래 보조 문구는 없어요 (제안 22).
const STEPS = ["접수", "검토중", "답변 완료"] as const;
const CURRENT_STEP = 0;

export function NavigatorContactCompletePage() {
  const navigate = useNavigate();
  const state = useLocation().state as { receipt?: InquiryReceipt; isMember?: boolean; from?: string } | null;
  if (!state?.receipt) return <Navigate to={NV_PATHS.home} replace />;
  const { receipt, isMember, from } = state;

  const rows = [
    ["담당 팀", receipt.team_name],
    ["문의 메일", receipt.team_email],
    ["접수 유형", receipt.inquiry_type_label],
  ];

  return (
    <div className="flex flex-1 flex-col items-center px-4 pb-40 pt-[120px] text-center">
      <div aria-hidden className="flex h-[88px] w-[88px] items-center justify-center rounded-full bg-nv-success text-nv-text-inverse">
        <NvCheckIcon size={44} />
      </div>
      <h1 className="mt-5 text-[28px] font-normal leading-[34px] text-nv-text-primary">문의가 접수되었습니다</h1>
      <p className="mt-5 text-[15px] leading-[18px] text-nv-text-secondary">담당자가 확인 후 빠른 시일 내에 답변드리겠습니다.</p>

      <ol aria-label="진행 단계" className="mt-7 flex items-start">
        {STEPS.map((label, index) => {
          const done = index < CURRENT_STEP;
          const current = index === CURRENT_STEP;
          const reached = done || current;
          return (
            <li key={label} aria-current={current ? "step" : undefined} className="flex items-start">
              {index > 0 && <span aria-hidden className="mx-[51px] mt-[13px] h-[2px] w-7 bg-[#DEE0E5]" />}
              <div className="relative h-[53px] w-7">
                <span
                  aria-hidden
                  className={cn(
                    "flex h-7 w-7 items-center justify-center rounded-full text-[13px] font-bold leading-4",
                    reached ? "bg-[#009966] text-nv-text-inverse" : "border-[1.5px] border-[#DEE0E5] bg-nv-surface text-[#8F9499]",
                  )}
                >
                  {reached ? "✓" : index + 1}
                </span>
                <span
                  className={cn(
                    "absolute left-1/2 top-9 -translate-x-1/2 whitespace-nowrap text-[14px] leading-[17px]",
                    current ? "font-bold text-nv-text-primary" : reached ? "font-medium text-nv-text-primary" : "font-medium text-[#8F9499]",
                  )}
                >
                  {label}
                </span>
              </div>
            </li>
          );
        })}
      </ol>

      <dl className="mt-7 flex w-full max-w-[480px] flex-col gap-3 rounded-nv-card bg-nv-bg-page p-6">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-center justify-between">
            <dt className="text-[13px] leading-4 text-nv-text-secondary">{label}</dt>
            <dd className="text-[14px] font-medium leading-[17px] text-nv-text-primary">{value}</dd>
          </div>
        ))}
      </dl>
      <div className="mt-7 flex items-stretch gap-[10px]">
        {isMember && (
          <NvButton variant="secondary" onClick={() => navigate(`${NV_PATHS.myPage}/inquiries`)}>
            문의 이력 바로가기
          </NvButton>
        )}
        <NvButton onClick={() => navigate(from && from !== "/contact" ? from : NV_PATHS.home)}>확인</NvButton>
      </div>
    </div>
  );
}
